import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getGRNList, createGRN, deleteGRN, getItems, createItem, getMembers } from '../../services/api';
import { FiPlus, FiTrash2, FiX, FiDownload, FiEye } from 'react-icons/fi';
import Toast from '../../components/Toast';

const GRN = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [viewGRN, setViewGRN] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), supplier: '', notes: '', source_type: 'purchase', donor_member_id: '' });
  const [lines, setLines] = useState([{ item_id: '', quantity: '', unit_price: '', total_price: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', type: 'consumable', unit: '', category: '' });
  const [addingItem, setAddingItem] = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const fetchList = () => getGRNList({ from, to }).then(r => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const fetchItems = () => getItems().then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});

  useEffect(() => { fetchList(); }, [from, to]);
  useEffect(() => { fetchItems(); }, []);
  useEffect(() => { getMembers({}).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const setLine = (i, field, value) => {
    setLines(prev => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const q = parseFloat(field === 'quantity' ? value : updated[i].quantity) || 0;
        const p = parseFloat(field === 'unit_price' ? value : updated[i].unit_price) || 0;
        updated[i].total_price = (q * p).toFixed(2);
      }
      return updated;
    });
  };

  const addLine = () => setLines(p => [...p, { item_id: '', quantity: '', unit_price: '', total_price: '' }]);
  const removeLine = (i) => setLines(p => p.filter((_, idx) => idx !== i));

  const grandTotal = lines.reduce((s, l) => s + (parseFloat(l.total_price) || 0), 0);

  const handleAddItem = async () => {
    if (!newItem.name.trim()) return;
    try {
      await createItem({ name: newItem.name.trim(), type: newItem.type, unit: newItem.unit || null, category: newItem.category || null });
      setNewItem({ name: '', type: 'consumable', unit: '', category: '' });
      setAddingItem(false);
      fetchItems();
      showToast('Item added');
    } catch (e) { showToast('Failed to add item', 'error'); }
  };

  const handleAdd = async () => {
    setError('');
    if (!form.date) return setError('Date is required');
    if (form.source_type === 'donation' && !form.donor_member_id) return setError('Pick the member who donated');
    const validLines = lines.filter(l => l.item_id && l.quantity);
    if (validLines.length === 0) return setError('Add at least one item');
    setSaving(true);
    try {
      await createGRN({ ...form, donor_member_id: form.source_type === 'donation' ? form.donor_member_id : null, items: validLines });
      setShowAdd(false);
      setForm({ date: new Date().toISOString().slice(0, 10), supplier: '', notes: '', source_type: 'purchase', donor_member_id: '' });
      setLines([{ item_id: '', quantity: '', unit_price: '', total_price: '' }]);
      showToast('GRN created');
      fetchList();
      fetchItems();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete ${r.grn_number}? Stock will be reversed.`)) return;
    await deleteGRN(r.id);
    showToast('Deleted', 'error');
    fetchList();
    fetchItems();
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.grn')}</h1><p>{list.length}</p></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus /> {t('form.newGRN')}</button>
      </div>

      <div className="filters-row">
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.from')}</label>
        <input className="filter-input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.to')}</label>
        <input className="filter-input" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </div>

      {/* Stock levels summary */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">{t('form.stockLevels')}</div>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {items.length === 0 ? <span style={{ fontSize: 13, color: '#6b7280' }}>{t('form.noItems')}</span> : items.map(i => (
            <div key={i.id} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
              <strong>{i.name}</strong>
              <span style={{ marginLeft: 8, color: parseFloat(i.current_stock) <= 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                {parseFloat(i.current_stock).toFixed(2)} {i.unit || ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr><th>#</th><th>GRN {t('finance.crNumber').replace('CR', 'GRN')}</th><th>{t('common.date')}</th><th>{t('table.supplier')}</th><th>{t('table.items')}</th><th>{t('common.total')}</th><th>{t('table.by')}</th><th></th></tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={8}><div className="empty-state"><div className="empty-icon"><FiDownload /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : list.map((r, i) => (
              <tr key={r.id}>
                <td data-label="#">{i + 1}</td>
                <td data-label={`GRN ${t('finance.crNumber').replace('CR', 'GRN')}`}><strong>{r.grn_number}</strong></td>
                <td data-label={t('common.date')}>{r.date}</td>
                <td data-label={t('table.supplier')}>{r.supplier || '—'}</td>
                <td data-label={t('table.items')}>{r.item_count}</td>
                <td data-label={t('common.total')}>{parseFloat(r.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td data-label={t('table.by')}>{r.created_by_name}</td>
                <td data-label="Actions">
                  <div className="action-btns">
                    <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 780 }}>
            <div className="modal-header">
              <div><h3>New Goods Received Note</h3></div>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid" style={{ marginBottom: 20 }}>
                <div className="form-group"><label>Date <span className="required">*</span></label><input type="date" value={form.date} onChange={set('date')} /></div>
                <div className="form-group">
                  <label>Source</label>
                  <select value={form.source_type} onChange={set('source_type')}>
                    <option value="purchase">Purchase (supplier)</option>
                    <option value="donation">Donation (member gift)</option>
                    <option value="existing">Existing (already owned)</option>
                  </select>
                </div>
                {form.source_type === 'donation' ? (
                  <div className="form-group">
                    <label>Donated by <span className="required">*</span></label>
                    <select value={form.donor_member_id} onChange={set('donor_member_id')}>
                      <option value="">— Select member —</option>
                      {members.map(m => <option key={m.id} value={m.id}>{[m.first_name, m.middle_name, m.last_name].filter(Boolean).join(' ')}</option>)}
                    </select>
                  </div>
                ) : (
                  <div className="form-group"><label>Supplier</label><input value={form.supplier} onChange={set('supplier')} placeholder={form.source_type === 'existing' ? 'Origin (optional)' : 'Supplier name'} /></div>
                )}
                <div className="form-group form-full"><label>Notes</label><input value={form.notes} onChange={set('notes')} /></div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <strong style={{ fontSize: 13 }}>Items</strong>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {addingItem ? (
                    <>
                      <input className="filter-input" value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} placeholder="Item name" style={{ width: 150 }} />
                      <select className="filter-input" value={newItem.type} onChange={e => setNewItem(p => ({ ...p, type: e.target.value }))} style={{ width: 130 }}>
                        <option value="consumable">Consumable</option>
                        <option value="fixed_asset">Fixed Asset</option>
                      </select>
                      <input className="filter-input" value={newItem.unit} onChange={e => setNewItem(p => ({ ...p, unit: e.target.value }))} placeholder="Unit (pcs)" style={{ width: 90 }} />
                      <button className="btn btn-sm btn-primary" onClick={handleAddItem}>Add</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setAddingItem(false)}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn btn-sm btn-secondary" onClick={() => setAddingItem(true)}>+ New Item</button>
                  )}
                  <button className="btn btn-sm btn-secondary" onClick={addLine}>+ Add Row</button>
                </div>
              </div>

              <table>
                <thead><tr><th>Item</th><th>Qty</th><th>Unit Price</th><th>Total</th><th></th></tr></thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <select value={l.item_id} onChange={e => setLine(i, 'item_id', e.target.value)} style={{ width: '100%' }}>
                          <option value="">— Select item —</option>
                          {items.map(item => <option key={item.id} value={item.id}>{item.type === 'fixed_asset' ? '🔧 ' : ''}{item.name} {item.unit ? `(${item.unit})` : ''}</option>)}
                        </select>
                      </td>
                      <td><input type="number" step="0.01" value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} style={{ width: 80 }} /></td>
                      <td><input type="number" step="0.01" value={l.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} style={{ width: 100 }} /></td>
                      <td style={{ fontWeight: 600 }}>{l.total_price || '0.00'}</td>
                      <td><button className="btn-icon danger" onClick={() => removeLine(i)}><FiTrash2 /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f9fafb' }}>
                    <td colSpan={3} style={{ padding: '10px 16px', fontWeight: 700 }}>Grand Total</td>
                    <td style={{ padding: '10px 16px', fontWeight: 700 }}>{grandTotal.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Save GRN'}</button>
            </div>
          </div>
        </div>
      )}
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default GRN;
